import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import LoadingSpinner from '../../components/LoadingSpinner';
import { debugAuthCallback, getEnvironmentInfo } from '../../utils/authDebug';

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Debug the current state
        getEnvironmentInfo();
        debugAuthCallback();
        
        console.log('🔄 Processing OAuth callback...');
        
        // Handle both hash-based (implicit flow) and query-based (PKCE flow) callbacks
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        
        // Check if we have hash-based tokens (implicit flow)
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (accessToken) {
          console.log('✅ Processing hash-based OAuth callback with access token');
          
          // For hash-based flow, we need to set the session manually
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });
          
          if (error) {
            console.error('❌ Error setting session from hash:', error);
            navigate('/signin?error=auth_callback_failed');
            return;
          }
          
          if (data?.session) {
            console.log('✅ Successfully set session from hash, redirecting to home');
            navigate('/');
            return;
          }
        }
        
        // Check for query-based callback (PKCE flow)
        const code = queryParams.get('code');
        if (code) {
          console.log('✅ Processing query-based OAuth callback with code');
          
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) {
            console.error('❌ Error exchanging code for session:', error);
            navigate('/signin?error=auth_callback_failed');
            return;
          }
          
          if (data?.session) {
            console.log('✅ Successfully exchanged code for session, redirecting to home');
            navigate('/');
            return;
          }
        }
        
        // Check for error in URL
        const error = queryParams.get('error') || hashParams.get('error');
        const errorDescription = queryParams.get('error_description') || hashParams.get('error_description');
        
        if (error) {
          console.error('❌ OAuth error in URL:', error, errorDescription);
          navigate(`/signin?error=${error}&description=${errorDescription}`);
          return;
        }
        
        // Fallback: try to get existing session
        console.log('⚠️ No tokens found in URL, checking existing session');
        const { data, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('❌ Auth callback error:', sessionError);
          navigate('/signin?error=auth_callback_failed');
          return;
        }

        if (data?.session) {
          console.log('✅ Found existing session, redirecting to home');
          navigate('/');
        } else {
          console.log('⚠️ No session found, redirecting to sign in');
          navigate('/signin?error=no_session');
        }
      } catch (error) {
        console.error('❌ Auth callback error:', error);
        navigate('/signin?error=auth_callback_failed');
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <LoadingSpinner size="lg" />
        <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
          Completing Sign In
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Please wait while we complete your authentication...
        </p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
